import React, { useContext, useState } from "react";
import { CalendarCheck2, CalendarOff, CalendarX2, Clock, CreditCard, EyeOff, Mail, TriangleAlert, Users } from "lucide-react";

import "./ownerCalendarCard.css";
import { CombinedContext } from "../../../context/combinedContext";
import companyBasedRequest from "../../../request/companyBasedRequest";
import translations from "../../../translations/translations";
import useModalDismiss from "../../common/useModalDismiss";

/* ────────────────────────────────────────────────────────────
   Whether the owner is one of the people customers can book.

   Some owners cut hair; some just run the place. The second kind used
   to have no way to say so — registration gave them a calendar, and the
   only way to make it read as somebody else was to rename their own
   login, which left the actual stylist unable to see their own day.

   Turning it off with appointments already on the books is the awkward
   case, and this card used to try to be clever about it: pick a colleague,
   hand the whole calendar across. That went wrong in the way bulk moves
   always do — the colleague was already booked at one of those hours, so
   the move quietly skipped the one appointment that mattered and the owner
   was told it had worked.

   So it no longer moves anything. The server answers with a real count,
   this card says plainly that those appointments will be cancelled, and
   the owner decides: go and reassign them in the calendar one at a time,
   or cancel them here on purpose. Nobody's haircut is moved or dropped
   by a settings toggle guessing on their behalf.

   Neither direction is a toggle you can undo by clicking again, which is
   why both go through a confirm first. Turning it off DELETES the owner's
   schedule row outright — hours, days off and overrides with it — and
   turning it back on hands them an empty one. Nothing in the card said so,
   so the only way to learn it was to lose a rota.
──────────────────────────────────────────────────────────── */

const fill = (template, values) => Object.entries(values).reduce((out, [k, v]) => out.replaceAll(`{{${k}}}`, v), template);

/* ── what this actually costs ─────────────────────────────────
   Mirrors getSeatCounts() in billableSeats.js and the ceiling in
   trialPolicy.js: the shop's FIRST calendar rides the base price, every
   calendar after it is a staff seat, and seats past the cap are free.

   Duplicated here on purpose. The alternative is a round-trip to price a
   button the owner has not pressed yet — and a confirm that says "this
   adds to your bill" when the invoice will not move is worse than one
   that says nothing at all. The numbers are small and the rule has one
   line; keeping it in step with the server is cheaper than being vague.
──────────────────────────────────────────────────────────── */
const MAX_BILLED_STAFF_SEATS = 4;

const billedSeatsFor = (calendars) => Math.min(Math.max(0, calendars - 1), MAX_BILLED_STAFF_SEATS);

const OwnerCalendarCard = ({ shopData, onChanged, showBanner }) => {
	const { userData, language } = useContext(CombinedContext);

	const [busy, setBusy] = useState(false);
	/* The direction the owner asked for, held while they read what it costs.
	   Null means no confirm is open; `false` is a real value here, so every
	   check on it has to be against null rather than truthiness. */
	const [confirm, setConfirm] = useState(null);
	/* The second, explicit yes — { count } from the server's 409. The only
	   state that can send cancelBookings, and the only place in the app where
	   a setting calls off somebody else's appointment. */
	const [cancelPlan, setCancelPlan] = useState(null);

	const t = translations[language].teamPage.ownerCalendar;

	/* The backdrop dismisses this one — nothing is typed into it, and a stray
	   click landing on "not today" is the harmless outcome. */
	const confirmDismiss = useModalDismiss(() => setConfirm(null), {
		enabled: confirm !== null && !busy,
		dismissOnBackdrop: true,
	});

	/* Same reasoning, and more so: the stray-click outcome here is keeping the
	   appointments, which is the direction nobody regrets. */
	const cancelDismiss = useModalDismiss(() => setCancelPlan(null), {
		enabled: !!cancelPlan && !busy,
		dismissOnBackdrop: true,
	});

	const isOwner = userData?.user?.role === "owner";
	// Absent means the owner holds a calendar — matches the server's default.
	const takesBookings = shopData?.ownerTakesBookings !== false;

	/* Staff hold a calendar once they're active; invited and registered-but-not-
	   launched people don't. With the owner opted out too, that's a shop nobody
	   can book. */
	const activeStaff = (shopData?.staffs || []).filter((s) => s.userId && s.status === "active").length;
	const noCalendars = !takesBookings && activeStaff === 0;

	if (!isOwner) return null;

	/* What the owner's next invoice does, in one line they can act on.

	   A delta of zero answers first, because it is true whatever the subscription
	   is doing. The two reasons a calendar is free are NOT interchangeable, and
	   the wrong one is a support ticket: the shop's first calendar rides the base
	   price, while anything past the billed ceiling is free no matter who holds
	   it. Only the second is a cap, and only the first is "included". */
	const billingLine = (next) => {
		const calendars = (takesBookings ? 1 : 0) + activeStaff;
		const delta = billedSeatsFor((next ? 1 : 0) + activeStaff) - billedSeatsFor(calendars);

		if (delta === 0) {
			if (activeStaff > MAX_BILLED_STAFF_SEATS) return fill(t.billingCapped, { max: MAX_BILLED_STAFF_SEATS + 1 });
			return next ? t.billingIncluded : t.billingSame;
		}

		/* Anything that is not a live subscription — never checked out, paused,
		   past due, cancelled — parks the number in pendingSeatCount for the
		   webhook to replay, so nothing moves on an invoice today.

		   A TRIAL is deliberately NOT one of these: the webhook counts trialing as
		   subscriptionActive, so it falls through to the two lines below, which is
		   exactly right. Paddle is sent do_not_bill while a trial runs, so "not
		   charged today, added to your next invoice" describes a trial precisely —
		   that invoice being the first one after it converts. */
		if (!shopData?.subscriptionActive) return t.billingPending;

		return delta > 0 ? t.billingUp : t.billingDown;
	};

	/* Everything the click is about to do that the card cannot show on its own.

	   Deliberately not a count of appointments: that number lives behind the
	   request, and waiting on it to draw the confirm would put a spinner between
	   the owner and a question they have already asked. The cancel step is where
	   the real figure appears, once the server has it. */
	const consequencesOf = (next) => {
		const lines = next
			? [
					{ key: "billing", Icon: CreditCard, text: billingLine(true) },
					{ key: "schedule", Icon: Clock, text: t.confirmOnSchedule },
					{ key: "visibility", Icon: CalendarCheck2, text: t.confirmOnVisibility },
				]
			: [
					{ key: "schedule", Icon: CalendarX2, text: t.confirmOffSchedule, tone: "is-loss" },
					{ key: "bookings", Icon: CalendarOff, text: t.confirmOffBookings, tone: "is-loss" },
					{ key: "visibility", Icon: EyeOff, text: t.confirmOffVisibility },
					{ key: "billing", Icon: CreditCard, text: billingLine(false) },
				];

		/* The shop stops being bookable entirely. The card already says this once
		   it has happened; saying it beforehand is the only version that helps. */
		if (!next && activeStaff === 0) {
			lines.push({ key: "empty", Icon: TriangleAlert, text: t.confirmOffNoCalendars, tone: "is-loss" });
		}

		return lines;
	};

	/* The cancel step says what the first confirm could not: a real number,
	   because by now the server has counted. Vagueness is what makes a click
	   regrettable, and "3 appointments" is the difference between a decision
	   and a shrug. */
	const cancelConsequences = (plan) => [
		{ key: "what", Icon: CalendarX2, text: fill(t.cancelWhat, { count: plan.count }), tone: "is-loss" },
		{ key: "emails", Icon: Mail, text: t.cancelEmails },
		/* The way out, and the reason this modal does not offer to do it for
		   them: reassigning is one booking at a time, where the person doing it
		   can see whose day they are putting it on. */
		{ key: "instead", Icon: CalendarCheck2, text: t.cancelReassignInstead },
		{ key: "schedule", Icon: Clock, text: t.confirmOffSchedule },
	];

	const apply = async (next, cancelBookings) => {
		setBusy(true);

		try {
			const res = await companyBasedRequest.setOwnerCalendar(
				shopData.shopId,
				{ ownerTakesBookings: next, cancelBookings },
				userData.cognitoTokens.AccessToken,
			);

			setCancelPlan(null);

			const cancelled = res.cancelledBookings || 0;

			if (cancelled > 0) {
				showBanner("success", fill(t.cancelledSuccess, { count: cancelled }), 7000);
			} else {
				showBanner("success", next ? t.onSuccess : t.offSuccess, 6000);
			}

			onChanged();
		} catch (err) {
			const payload = err?.payload;

			/* `canCancel` is the server telling us this refusal has a way out. An
			   older deploy does not send it, so we fall back to the banner rather
			   than offering a button that would come back with the same 409. */
			if (payload?.error === "bookings_will_be_cancelled" && payload?.canCancel) {
				setCancelPlan({ count: payload.bookingCount || 0 });
			} else if (payload?.bookingCount) {
				/* Every shape the old hand-over endpoint used to refuse with. They
				   all mean the same thing now — there are appointments in the way —
				   so say so rather than rendering "(409 schedule_conflicts)". */
				setCancelPlan(null);
				showBanner("error", fill(t.blocked, { count: payload.bookingCount }), 9000);
			} else {
				/* Say what actually failed. A plain "we couldn't do that" is
				   indistinguishable from the button doing nothing, which is exactly how
				   a not-yet-deployed endpoint reads: 404, no detail, silent no-op. */
				const detail = err?.status ? ` (${err.status}${payload?.error ? ` ${payload.error}` : ""})` : "";
				console.error("setOwnerCalendar failed:", err?.status, payload || err?.message);
				showBanner("error", `${t.failed}${detail}`, 8000);
				setCancelPlan(null);
			}
		} finally {
			setBusy(false);
			/* Last, so the 409 above has already opened the cancel step — the
			   confirm hands straight over to it rather than blinking the card in
			   between. Note this does NOT close `cancelPlan`: the branch above is
			   the only thing that opens it, and clearing it here would shut it in
			   the same tick it was raised. */
			setConfirm(null);
		}
	};

	return (
		<>
			<section className={`sf-oc-card ${takesBookings ? "is-on" : "is-off"}`}>
				<span className="sf-oc-icon" aria-hidden="true">
					{takesBookings ? <CalendarCheck2 size={19} strokeWidth={2} /> : <Users size={19} strokeWidth={2} />}
				</span>

				<div className="sf-oc-copy">
					<h2 className="sf-oc-label">{t.label}</h2>
					<p className="sf-oc-state">{takesBookings ? t.on : t.off}</p>

					{/* The dead end worth calling out: nobody here can be booked at all.
					    The roster still lists the owner, so an empty team no longer reads
					    as empty — this is what says the shop can't take a booking yet. */}
					{noCalendars ? <p className="sf-oc-warn">{t.noCalendars}</p> : <p className="sf-oc-note">{t.pricingNote}</p>}
				</div>

				<button type="button" className="sf-oc-switch" onClick={() => setConfirm(!takesBookings)} disabled={busy}>
					{busy && !cancelPlan && confirm === null ? t.working : takesBookings ? t.turnOff : t.turnOn}
				</button>
			</section>

			{confirm !== null && (
				<div className="sf-oc-overlay" role="dialog" aria-modal="true" aria-labelledby="sf-oc-confirm-title" {...confirmDismiss}>
					<div className="sf-oc-modal">
						<span className={`sf-oc-modal-icon ${confirm ? "" : "is-loss"}`} aria-hidden="true">
							{confirm ? <CalendarCheck2 size={22} strokeWidth={2} /> : <TriangleAlert size={22} strokeWidth={2} />}
						</span>

						<h3 className="sf-oc-modal-title" id="sf-oc-confirm-title">
							{confirm ? t.confirmOnTitle : t.confirmOffTitle}
						</h3>

						<ul className="sf-oc-consequences">
							{consequencesOf(confirm).map(({ key, Icon, text, tone }) => (
								<li key={key} className={`sf-oc-consequence ${tone || ""}`}>
									<span className="sf-oc-consequence-icon" aria-hidden="true">
										<Icon size={15} strokeWidth={2} />
									</span>
									<span className="sf-oc-consequence-text">{text}</span>
								</li>
							))}
						</ul>

						<div className="sf-oc-modal-actions">
							<button type="button" className="sf-oc-btn" onClick={() => setConfirm(null)} disabled={busy}>
								{t.cancel}
							</button>

							<button
								type="button"
								className={`sf-oc-btn ${confirm ? "is-primary" : "is-loss"}`}
								onClick={() => apply(confirm)}
								disabled={busy}
							>
								{busy ? t.working : confirm ? t.confirmOn : t.confirmOff}
							</button>
						</div>
					</div>
				</div>
			)}

			{cancelPlan && (
				<div className="sf-oc-overlay" role="dialog" aria-modal="true" aria-labelledby="sf-oc-cancel-title" {...cancelDismiss}>
					<div className="sf-oc-modal">
						<span className="sf-oc-modal-icon is-loss" aria-hidden="true">
							<CalendarX2 size={22} strokeWidth={2} />
						</span>

						<h3 className="sf-oc-modal-title" id="sf-oc-cancel-title">
							{fill(t.cancelTitle, { count: cancelPlan.count })}
						</h3>

						<ul className="sf-oc-consequences">
							{cancelConsequences(cancelPlan).map(({ key, Icon, text, tone }) => (
								<li key={key} className={`sf-oc-consequence ${tone || ""}`}>
									<span className="sf-oc-consequence-icon" aria-hidden="true">
										<Icon size={15} strokeWidth={2} />
									</span>
									<span className="sf-oc-consequence-text">{text}</span>
								</li>
							))}
						</ul>

						<div className="sf-oc-modal-actions">
							{/* Not "cancel" — that word is already taken by what the other
							    button does to somebody's haircut. */}
							<button type="button" className="sf-oc-btn" onClick={() => setCancelPlan(null)} disabled={busy}>
								{t.keepThem}
							</button>

							<button type="button" className="sf-oc-btn is-loss" onClick={() => apply(false, true)} disabled={busy}>
								{busy ? t.working : t.cancelConfirm}
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default OwnerCalendarCard;
